import { type DataOptions, useLazyData } from '../../graphQl'
import {
  type PreliminaryQuotesRequestPayload,
  type FindAddressesPayload,
  type FindAddressesResponse,
  type PreliminaryQuotesResponse,
} from './quotes.types'
import * as queries from './quotes.queries'
import { useAnalytics } from '../../analytics'

/**
 * fetch preliminary quotes (lazy query)
 */
export const usePreliminaryQuotes = (dataOptions?: DataOptions) => {
  const { fetch, ...data } = useLazyData<PreliminaryQuotesResponse>(
    queries.getPreliminaryQuotes,
    dataOptions,
  )
  const { pushQuotesRequestedEvent } = useAnalytics()

  const fetchPreliminaryQuotes = async (
    preliminaryQuotesRequest?: PreliminaryQuotesRequestPayload, promoCode?: string) => {
    try {
      const response = await fetch({ preliminaryQuotesRequest, promoCode })

      /* tracking "online" quotes */

      const nbQuotesReceived = response.totalQuotes ?? 0
      const cheapestPriceReceived = response.preliminaryQuotes?.[0]?.price
      const fromCity = response.resolvedOriginAddress?.city
      const fromRegion = response.resolvedOriginAddress?.region
      const toCity = response.resolvedDestinationAddress?.city
      const toRegion = response.resolvedDestinationAddress?.region

      pushQuotesRequestedEvent({
        nbQuotesReceived,
        cheapestPriceReceived,
        fromCity,
        fromRegion,
        toCity,
        toRegion,
      })

      return response
    } catch (error: any) {
      /* tracking "agent" quotes */

      pushQuotesRequestedEvent({
        nbQuotesReceived: 0,
      })

      throw new Error(error.message)
    }
  }

  return {
    fetchPreliminaryQuotes,
    ...data,
  }
}

/**
 * find addresses (lazy query)
 */
export const useFindAddresses = (dataOptions?: DataOptions) => {
  const { fetch, ...data } = useLazyData<FindAddressesResponse>(
    queries.findAddresses,
    dataOptions,
  )

  const findAddresses = async (payload: FindAddressesPayload) => {
    if (!payload.origin?.trim() && !payload.destination?.trim()) {
      return
    }
    return await fetch({ payload })
  }

  return {
    findAddresses,
    ...data,
  }
}
